import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { getCustomerPortalDashboardSummary } from '../../api/customerPortalApi';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { AttentionList } from '../../components/dashboard/AttentionList';
import {
  DashboardEmptyState,
  MiniStatusDonut,
} from '../../components/dashboard/DashboardVisuals';
import { DetailField } from '../../components/InfoCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { MetricCard } from '../../components/MetricCard';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { Button } from '../../components/ui/Button';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import { getSubscriptionStatusLabel } from '../../utils/statusLabels';
import {
  buildPortalAttentionItems,
  mapPortalDashboardSummary,
} from '../../utils/dashboardSummaryMappers';

const { semantic } = colorTokens;

const metricGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr 1fr',
    md: '1fr 1fr 1fr',
  },
  gap: 2,
};

const summaryGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  gap: 2.5,
};

const mutedSx = {
  fontSize: 12,
  lineHeight: '16px',
  color: semantic.text.muted,
};

function createEmptyPortalStats(emptyValue) {
  return {
    companyId: null,
    companyName: null,
    tenants: emptyValue,
    tenantsRaw: null,
    openTickets: emptyValue,
    openTicketsRaw: null,
    subscription: emptyValue,
    subscriptionRaw: '',
    paymentIssues: 0,
    paymentIssuesAvailable: false,
    setupSummary: emptyValue,
    setupCompleted: 0,
    setupInProgress: 0,
    setupBlocked: 0,
    setupNotStarted: 0,
    readinessAvailable: false,
    provisioningIssues: 0,
    schemaIssues: 0,
    planName: emptyValue,
    amountDue: emptyValue,
    paymentStatus: emptyValue,
    paidUntil: null,
    graceUntil: null,
    billingAvailable: false,
    supportAvailable: false,
    latestTicket: null,
    modulesCount: emptyValue,
    modulesActiveCount: null,
    attention: [],
  };
}

export function CustomerPortalDashboardPage() {
  const { t } = useTranslation([
    'dashboard',
    'common',
    'setupWizard',
    'tenantProvisioning',
    'tenantSchema',
    'billing',
    'statuses',
  ]);
  const { activeCustomer, activeCustomerId, loading } = useCustomerPortal();
  const emptyValue = t('common:empty_value');
  const [stats, setStats] = useState(() => createEmptyPortalStats(emptyValue));
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (!activeCustomerId) {
      return undefined;
    }

    let cancelled = false;
    setStatsLoading(true);
    // Backend summary is authoritative for section visibility (incl. support-mode).
    // Do not pre-gate on frontend hasPermission.
    getCustomerPortalDashboardSummary(activeCustomerId)
      .then((summary) => {
        if (cancelled) {
          return;
        }
        setStats(mapPortalDashboardSummary(summary, {
          emptyValue,
          t,
          getSubscriptionStatusLabel,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setStats(createEmptyPortalStats(emptyValue));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStatsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeCustomerId, emptyValue, t]);

  const setupSegments = useMemo(() => [
    {
      key: 'completed',
      label: t('dashboard:portal.setup_completed'),
      value: stats.setupCompleted,
      color: semantic.success,
    },
    {
      key: 'in_progress',
      label: t('dashboard:portal.setup_in_progress'),
      value: stats.setupInProgress,
      color: semantic.info,
    },
    {
      key: 'blocked',
      label: t('dashboard:portal.setup_blocked'),
      value: stats.setupBlocked,
      color: semantic.error,
    },
    {
      key: 'not_started',
      label: t('dashboard:portal.setup_not_started'),
      value: stats.setupNotStarted,
      color: semantic.text.muted,
    },
  ], [stats, t]);

  const attentionItems = useMemo(
    () => buildPortalAttentionItems(stats.attention, t),
    [stats.attention, t],
  );

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!activeCustomer) {
    return (
      <SectionCard variant="Default" sx={{ mb: 0 }}>
        <Alert severity="info" sx={{ bgcolor: 'transparent', p: 0 }}>
          {t('dashboard:no_membership')}
        </Alert>
      </SectionCard>
    );
  }

  const setupTotal = setupSegments.reduce((sum, item) => sum + (item.value || 0), 0);
  const companyName = stats.companyName || activeCustomer.name;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dashboard:title')}
        subtitle={t('dashboard:active_company', { name: companyName })}
      />

      <Stack spacing={2.5}>
        <SectionCard
          variant="Default"
          title={t('dashboard:overview_title')}
          subtitle={t('dashboard:overview_subtitle')}
          sx={{ mb: 0 }}
        >
          {statsLoading ? (
            <LoadingSkeleton type="CardGrid" rows={2} />
          ) : (
            <Box sx={metricGridSx}>
              <MetricCard
                value={stats.tenants}
                label={t('dashboard:metrics.tenants')}
                to="/portal/tenants"
                helper={stats.tenantsRaw == null ? t('dashboard:metric_unavailable') : ''}
              />
              <MetricCard
                value={stats.openTickets}
                label={t('dashboard:metrics.open_tickets')}
                to="/portal/support"
                helper={stats.openTicketsRaw == null ? t('dashboard:metric_unavailable') : ''}
              />
              <MetricCard
                value={stats.subscription}
                label={t('dashboard:metrics.subscription_status')}
                to="/portal/billing"
                helper={!stats.billingAvailable ? t('dashboard:metric_unavailable') : ''}
              />
              <MetricCard
                value={stats.paymentIssuesAvailable ? stats.paymentIssues : emptyValue}
                label={t('dashboard:metrics.payment_required')}
                to="/portal/billing"
                helper={!stats.paymentIssuesAvailable ? t('dashboard:metric_unavailable') : ''}
              />
            </Box>
          )}
        </SectionCard>

        <Box sx={summaryGridSx}>
          <SectionCard
            variant="Default"
            title={t('dashboard:portal.readiness_title')}
            subtitle={t('dashboard:portal.readiness_subtitle')}
            sx={{ mb: 0 }}
          >
            {statsLoading ? (
              <LoadingSkeleton type="Detail" />
            ) : !stats.readinessAvailable ? (
              <DashboardEmptyState message={t('dashboard:metric_unavailable')} />
            ) : (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <DetailField label={t('dashboard:portal.billing_status')}>
                    {stats.subscriptionRaw ? (
                      <StatusBadge
                        status={stats.subscriptionRaw}
                        category="subscription"
                        apiLabel={stats.subscription}
                        size="Sm"
                      />
                    ) : emptyValue}
                  </DetailField>
                  <DetailField label={t('dashboard:portal.tenant_setup')}>
                    <Typography sx={{ fontSize: 13, color: semantic.text.primary }}>
                      {stats.setupSummary}
                    </Typography>
                  </DetailField>
                  <DetailField label={t('dashboard:portal.tech_status')}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <StatusBadge
                        status={stats.provisioningIssues > 0 ? 'attention' : 'ok'}
                        category="generic"
                        apiLabel={stats.provisioningIssues > 0
                          ? t('dashboard:portal.provisioning_attention')
                          : t('dashboard:portal.provisioning_ok')}
                        size="Sm"
                        tone={stats.provisioningIssues > 0 ? 'Warning' : 'Success'}
                      />
                      <StatusBadge
                        status={stats.schemaIssues > 0 ? 'attention' : 'ok'}
                        category="generic"
                        apiLabel={stats.schemaIssues > 0
                          ? t('dashboard:portal.schema_attention')
                          : t('dashboard:portal.schema_ok')}
                        size="Sm"
                        tone={stats.schemaIssues > 0 ? 'Warning' : 'Success'}
                      />
                    </Stack>
                  </DetailField>
                </Box>
                {setupTotal > 0 ? (
                  <MiniStatusDonut
                    segments={setupSegments}
                    centerValue={setupTotal}
                    centerLabel={t('dashboard:metrics.tenants')}
                    size={104}
                  />
                ) : (
                  <DashboardEmptyState message={t('dashboard:portal.setup_empty')} />
                )}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            variant="Default"
            title={t('dashboard:portal.attention_title')}
            subtitle={t('dashboard:portal.attention_subtitle')}
            sx={{ mb: 0 }}
          >
            {statsLoading ? (
              <LoadingSkeleton type="Detail" />
            ) : (
              <AttentionList
                items={attentionItems}
                emptyMessage={t('dashboard:portal.attention_empty')}
              />
            )}
          </SectionCard>
        </Box>

        <Box sx={summaryGridSx}>
          <SectionCard
            variant="Default"
            title={t('dashboard:portal.billing_summary')}
            actions={(
              <Button variant="Ghost" component={RouterLink} to="/portal/billing" sx={{ minHeight: 32 }}>
                {t('dashboard:portal.open_billing')}
              </Button>
            )}
            sx={{ mb: 0 }}
          >
            {statsLoading ? (
              <LoadingSkeleton type="Detail" />
            ) : !stats.billingAvailable ? (
              <DashboardEmptyState message={t('dashboard:metric_unavailable')} />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <DetailField
                  label={t('billing:current_plan')}
                  value={stats.planName}
                />
                <DetailField
                  label={t('billing:amount_due')}
                  value={stats.amountDue}
                />
                <DetailField label={t('billing:payment_status')}>
                  {stats.paymentStatus !== emptyValue ? (
                    <StatusBadge
                      status={stats.paymentStatus}
                      category="generic"
                      apiLabel={stats.paymentStatus}
                      size="Sm"
                    />
                  ) : emptyValue}
                </DetailField>
              </Box>
            )}
          </SectionCard>

          <SectionCard
            variant="Default"
            title={t('dashboard:portal.support_summary')}
            actions={(
              <Button variant="Ghost" component={RouterLink} to="/portal/support" sx={{ minHeight: 32 }}>
                {t('dashboard:portal.open_support')}
              </Button>
            )}
            sx={{ mb: 0 }}
          >
            {statsLoading ? (
              <LoadingSkeleton type="Detail" />
            ) : !stats.supportAvailable ? (
              <DashboardEmptyState message={t('dashboard:metric_unavailable')} />
            ) : (
              <Stack spacing={1.5}>
                <DetailField label={t('dashboard:portal.latest_ticket')}>
                  {stats.latestTicket ? (
                    <Typography
                      sx={{
                        fontFamily: typographyTokens.fontFamily,
                        fontSize: 13,
                        lineHeight: '18px',
                        color: semantic.text.primary,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {stats.latestTicket.theme
                        || stats.latestTicket.subject
                        || stats.latestTicket.public_number
                        || emptyValue}
                    </Typography>
                  ) : (
                    <Typography sx={mutedSx}>{t('dashboard:portal.no_latest_ticket')}</Typography>
                  )}
                </DetailField>
              </Stack>
            )}
          </SectionCard>
        </Box>

        <SectionCard
          variant="Default"
          title={t('dashboard:portal.modules_summary')}
          actions={(
            <Button variant="Ghost" component={RouterLink} to="/portal/entitlements" sx={{ minHeight: 32 }}>
              {t('dashboard:portal.open_modules')}
            </Button>
          )}
          sx={{ mb: 0 }}
        >
          <Stack spacing={1.25}>
            <DetailField
              label={t('dashboard:portal.active_modules')}
              value={stats.modulesCount}
            />
            <Typography sx={mutedSx}>
              {t('dashboard:portal.modules_unavailable')}
            </Typography>
          </Stack>
        </SectionCard>
      </Stack>
    </Box>
  );
}
